import AppRoute from 'src/router/app-route';
import { useAuthStore } from 'stores/auth-store';

export const Route = {
    AboutUs: 'AboutUs',
    Dashboard: 'Dashboard',
    EmailLogin: 'EmailLogin',
    FAQs: 'FAQs',
    Home: 'Home',
    Login: 'Login',
    LoginCallback: 'LoginCallback',
    ManageAccount: 'ManageAccount',
    ManageAuth: 'ManageAuth',
    ManageInviteLinks: 'ManageInviteLinks',
    ManageProfileLinks: 'ManageProfileLinks',
    ManageUserProfile: 'ManageUserProfile',
    MarketClassification: 'MarketClassification',
    MarketLocation: 'MarketLocation',
    MarketOverview: 'MarketOverview',
    Methodology: 'Methodology',
    PageNotFound: 'PageNotFound',
    PrivacyPolicy: 'PrivacyPolicy',
    PublicInviteLink: 'PublicInviteLink',
    PublicUserProfileByAlias: 'PublicUserProfileByAlias',
    PublicUserProfileByToken: 'PublicUserProfileByToken',
    Setup: 'Setup',
    TermsOfService: 'TermsOfService',
    UnknownError: 'UnknownError',
    UserProfileRestricted: 'UserProfileRestricted',
    UserProfileSuspended: 'UserProfileSuspended'
};

export default [
    {
        path: '',
        component: () => import('layouts/MainLayout.vue'),
        meta: {
            public: true
        },
        children: [
            {
                name: Route.Home,
                path: '',
                component: () => import('pages/LandingPage.vue')
            },
            {
                name: Route.AboutUs,
                path: '/about',
                component: () => import('pages/AboutUsPage.vue')
            },
            {
                name: Route.FAQs,
                path: '/faqs',
                component: () => import('pages/FAQsPage.vue')
            },
            {
                name: Route.MarketOverview,
                path: '/market',
                component: () => import('pages/MarketOverviewPage.vue')
            },
            {
                name: Route.MarketClassification,
                path: '/market/:classificationSlug',
                component: () => import('pages/MarketClassificationPage.vue')
            },
            {
                name: Route.MarketLocation,
                path: '/market/in/:locationSlug',
                component: () => import('pages/MarketLocationPage.vue')
            },
            {
                name: Route.Methodology,
                path: '/methodology',
                component: () => import('pages/MethodologyPage.vue')
            },
            {
                name: Route.PrivacyPolicy,
                path: '/legal/privacy',
                component: () => import('pages/PrivacyPolicyPage.vue')
            },
            {
                name: Route.TermsOfService,
                path: '/legal/terms',
                component: () => import('pages/TermsOfServicePage.vue')
            },
            {
                // Retired with the community it governed; the terms carry the conduct rules it set out.
                path: '/guidelines',
                meta: {
                    permanentRedirect: AppRoute.build(Route.TermsOfService)
                },
                component: () => import('pages/PermanentRedirectPage.vue')
            }
        ]
    },
    {
        path: '/login',
        component: () => import('layouts/BaseLayout.vue'),
        meta: {
            robots: 'noindex,follow'
        },
        beforeEnter: (to, from) => {
            if (useAuthStore().userAuthenticated) {
                return AppRoute.build(Route.Dashboard);
            }
            return true;
        },
        children: [
            {
                path: '',
                name: Route.LoginCallback,
                component: () => import('pages/LoginPage.vue'),
                meta: {
                    public: true,
                    robots: 'noindex,follow'
                }
            }
        ]
    },
    {
        path: '',
        component: () => import('layouts/BaseLayout.vue'),
        beforeEnter: (to, from) => {
            if ([ Route.Login, Route.EmailLogin ].includes(to.name) && useAuthStore().userAuthenticated) {
                return AppRoute.build(Route.Dashboard);
            }
            return true;
        },
        children: [
            {
                name: Route.Login,
                path: '/app/login',
                component: () => import('pages/LoginPage.vue'),
                meta: {
                    public: true,
                    robots: 'noindex,follow'
                }
            },
            {
                name: Route.Setup,
                path: '/app/setup',
                component: () => import('components/UserWelcomeWizardDialog.vue'),
                meta: {
                    robots: 'noindex,follow'
                },
                beforeEnter: (to, from) => {
                    const authStore = useAuthStore();
                    if (authStore.user?.isReady) {
                        return AppRoute.build(Route.ManageUserProfile);
                    }
                    return true;
                }
            },
            {
                name: Route.PublicInviteLink,
                path: '/r/:token',
                component: () => import('pages/PublicInviteLinkPage.vue'),
                meta: {
                    public: true,
                    // The token is the whole authorization for the invite, so it must not reach analytics.
                    redactedParams: ['token'],
                    robots: 'noindex,nofollow'
                }
            }
        ]
    },
    {
        path: '',
        component: () => import('layouts/MainLayout.vue'),
        children: [
            {
                name: Route.PublicUserProfileByAlias,
                path: '/p/:alias',
                meta: { public: true },
                component: () => import('pages/PublicUserProfilePage.vue')
            },
            {
                name: Route.PublicUserProfileByToken,
                path: '/t/:token',
                // The token is what opens a profile that is not reachable by alias, so it must not reach analytics.
                meta: {
                    public: true,
                    redactedParams: ['token'],
                    robots: 'noindex,nofollow'
                },
                component: () => import('pages/PublicUserProfilePage.vue')
            }
        ]
    },
    {
        path: '/app',
        component: () => import('layouts/MainLayout.vue'),
        meta: {
            robots: 'noindex,follow'
        },
        children: [
            {
                name: Route.Dashboard,
                path: '',
                component: () => import('pages/DashboardPage.vue')
            },
            {
                name: Route.ManageAccount,
                path: 'account',
                component: () => import('pages/ManageAccountPage.vue')
            },
            {
                name: Route.ManageAuth,
                path: 'auth',
                component: () => import('pages/ManageSecurityPage.vue')
            },
            {
                name: Route.ManageInviteLinks,
                path: 'invite-links',
                meta: { requiresProfile: true },
                component: () => import('pages/ManageInviteLinksPage.vue')
            },
            {
                name: Route.ManageUserProfile,
                path: 'profile',
                beforeEnter: (to, from) => {
                    if (!useAuthStore().user?.isReady) {
                        return AppRoute.build(Route.Setup);
                    }
                    return true;
                },
                component: () => import('pages/ManageUserProfilePage.vue')
            },
            {
                name: Route.ManageProfileLinks,
                path: 'profile-links',
                meta: {
                    requiresProfile: true,
                    roles: [ 'Xprt' ]
                },
                component: () => import('pages/ManageProfileLinksPage.vue')
            }
        ]
    },
    {
        component: () => import('layouts/MainLayout.vue'),
        path: '/error',
        meta: {
            public: true
        },
        children: [
            {
                name: Route.PageNotFound,
                path: '/page-not-found',
                component: () => import('pages/error-pages/PageNotFoundErrorPage.vue'),
                meta: { errorStatusCode: 404 }
            },
            {
                name: Route.UserProfileRestricted,
                path: '/profile-restricted',
                component: () => import('pages/error-pages/UserProfileRestrictedErrorPage.vue'),
                meta: { errorStatusCode: 403 }
            },
            {
                name: Route.UserProfileSuspended,
                path: '/profile-suspended',
                component: () => import('pages/error-pages/UserProfileSuspendedErrorPage.vue'),
                meta: { errorStatusCode: 403 }
            },
            {
                name: Route.UnknownError,
                path: '/unknown-error',
                component: () => import('pages/error-pages/UnknownErrorPage.vue'),
                meta: { errorStatusCode: 500 }
            }
        ]
    },
    // An address that names nothing is answered where it was asked, under a 404, rather than sent on to the error
    // route. A redirect left the URL that failed without a status of its own, and a logged-out visitor never reached
    // the redirect at all, because the route was not public and the login page answered it instead.
    {
        path: '/:catchAll(.*)*',
        component: () => import('layouts/MainLayout.vue'),
        meta: {
            public: true
        },
        children: [
            {
                path: '',
                component: () => import('pages/error-pages/PageNotFoundErrorPage.vue'),
                meta: { errorStatusCode: 404 }
            }
        ]
    }
];
